import { TABLE_ROW_MIN_HEIGHT } from "@library-elements/table";
import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTableUpdateRowHeights } from "./use-table-update-row-heights";

interface IProps {
  width: string;
  height: number;
  tableCols: number;
  tableRows: number;
  tableWrapperRef: MutableRefObject<HTMLDivElement | null>;
  colsMinWidth: MutableRefObject<number[]>;
}

export const useTableResize = ({
  tableCols,
  tableRows,
  width,
  height,
  tableWrapperRef,
  colsMinWidth,
}: IProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [resizeWidth, setResizeWidth] = useState<string>(String(width || 100));
  const [resizeHeight, setResizeHeight] = useState<number>(height);
  const [elementDomWidth, setElementDomWidth] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);

  const { updateTableRowHeights } = useTableUpdateRowHeights();

  // Memoize `emitWidth` and `emitHeight` so consumers passing them through
  // a memoized `editorData` object don't see a fresh function identity each
  // render — that would invalidate the editorData reference and cascade into
  // every cell re-rendering on any parent render.
  const emitWidth = useCallback((width: string): void => {
    const wrapperEl = tableWrapperRef.current;
    if (!wrapperEl) return;
    const parent = wrapperEl.parentElement;
    if (!parent) return;
    const maxWidth = parent.getBoundingClientRect().width;

    const tableWidth = maxWidth * (Number(width) / 100);

    const tableMinWidth = colsMinWidth.current.reduce(
      (prev, cur) => prev + cur,
      0,
    );

    if (tableWidth < tableMinWidth) {
      // Cap at 100% so the element never exceeds its parent container. When
      // content genuinely can't fit, individual cells will clip/scroll rather
      // than stretching the whole element's outline past the slide edge.
      const needed = (tableMinWidth / maxWidth) * 100;
      setResizeWidth(Math.min(100, needed).toString());
      return;
    }

    setResizeWidth(width);
  }, [colsMinWidth, tableWrapperRef]);

  const emitHeight = useCallback((height: number): void => {
    const wrapperEl = tableWrapperRef.current;
    if (!wrapperEl) return;
    const wrapperRect = wrapperEl.getBoundingClientRect();

    const rows = wrapperEl.querySelectorAll(
      "tbody tr",
    ) as NodeListOf<HTMLElement>;

    const heights: number[] = [];
    rows.forEach((rowEl) => {
      const rowRect = rowEl.getBoundingClientRect();
      const per = rowRect.height / wrapperRect.height;
      heights.push(Math.max(TABLE_ROW_MIN_HEIGHT, height * per));
    });

    rows.forEach((rowEl, idx) => {
      rowEl.style.setProperty("height", `${heights[idx]}px`);
    });
    updateTableRowHeights(heights);
    setResizeHeight(Math.max(TABLE_ROW_MIN_HEIGHT * tableRows, height));
  }, [tableRows, tableWrapperRef, updateTableRowHeights]);

  useEffect(() => {
    if (containerRef.current) {
      const elDomWidth = containerRef.current.clientWidth;
      setElementDomWidth(elDomWidth);

      if (tableWrapperRef.current) {
        const tableRect = tableWrapperRef.current.getBoundingClientRect();
        setContainerWidth(tableRect.width);
        setContainerHeight(tableWrapperRef.current.clientHeight);
        emitWidth(Math.round((tableRect.width / elDomWidth) * 100).toString());
      }
    }
  }, [tableCols]);

  useEffect(() => {
    const wrapperEl = tableWrapperRef.current;
    if (!wrapperEl) return;

    const resizeObserver = new ResizeObserver(() => {
      const wrapperRect = wrapperEl.getBoundingClientRect();
      setContainerWidth(wrapperRect.width);
      setContainerHeight(wrapperRect.height);
    });

    resizeObserver.observe(wrapperEl);

    return () => {
      resizeObserver.disconnect();
    };
    // tableWrapperRef is a ref object — its `.current` is non-reactive, so
    // listing it as a dep doesn't trigger re-runs when the node attaches.
    // Run on mount; the body guards against a missing ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      setElementDomWidth(containerRef.current.clientWidth);
    }

    const wrapperEl = tableWrapperRef.current;
    if (!wrapperEl) return;
    const wrapperRect = wrapperEl.getBoundingClientRect();
    setContainerWidth(wrapperRect.width);
    setContainerHeight(wrapperRect.height);
  }, [resizeWidth, resizeHeight, updateTableRowHeights]);

  useEffect(() => {
    setResizeWidth(String(width));
    setResizeHeight(height);
  }, [width, height]);

  return {
    tableWrapperRef,
    containerRef,
    resizeWidth,
    resizeHeight,
    elementDomWidth,
    containerWidth,
    containerHeight,
    emitWidth,
    emitHeight,
  };
};
